/**
 * Restaurant multi-stock: Bar / Cuisine / Grill — same Lot/FIFO engine.
 * Product defaultStockLocation drives where recipe and POS deductions apply.
 */

import {
  isRestaurantBusinessType,
  isRestaurantDishProduct,
  isRestaurantSellableStockProduct
} from '../../utils/restaurantMode';

export const RESTAURANT_LOCATION_BAR = 'Bar';
export const RESTAURANT_LOCATION_CUISINE = 'Cuisine';
export const RESTAURANT_LOCATION_GRILL = 'Grill';

export const RESTAURANT_STOCK_LOCATIONS = Object.freeze([
  RESTAURANT_LOCATION_BAR,
  RESTAURANT_LOCATION_CUISINE,
  RESTAURANT_LOCATION_GRILL
]);

const LOCATION_ORDER = Object.freeze([
  RESTAURANT_LOCATION_BAR,
  RESTAURANT_LOCATION_CUISINE,
  RESTAURANT_LOCATION_GRILL,
  'Principal'
]);

const ALIASES = Object.freeze({
  bar: RESTAURANT_LOCATION_BAR,
  comptoir: RESTAURANT_LOCATION_BAR,
  caisse: RESTAURANT_LOCATION_BAR,
  cuisine: RESTAURANT_LOCATION_CUISINE,
  kitchen: RESTAURANT_LOCATION_CUISINE,
  kuche: RESTAURANT_LOCATION_CUISINE,
  küche: RESTAURANT_LOCATION_CUISINE,
  grill: RESTAURANT_LOCATION_GRILL,
  griller: RESTAURANT_LOCATION_GRILL,
  bbq: RESTAURANT_LOCATION_GRILL,
  principal: 'Principal',
  main: 'Principal'
});

/**
 * @param {unknown} location
 * @returns {string}
 */
export function normalizeStockLocationKey(location) {
  const raw = String(location || '')
    .trim()
    .replace(/\s+/g, ' ');
  if (!raw) return '';
  const lower = raw.toLowerCase();
  if (ALIASES[lower]) return ALIASES[lower];
  for (const known of RESTAURANT_STOCK_LOCATIONS) {
    if (known.toLowerCase() === lower) return known;
  }
  return raw;
}

/**
 * True only for restaurant business / explicit restaurant context.
 * Never invents Bar / Cuisine / Grill for magasin, pharmacy, etc.
 *
 * @param {{ businessType?: unknown, restaurantContext?: boolean }} [opts]
 */
export function isRestaurantStockLocationContext(opts = {}) {
  if (opts?.restaurantContext === true) return true;
  if (opts?.restaurantContext === false) return false;
  if (opts?.businessType != null) return isRestaurantBusinessType(opts.businessType);
  return false;
}

/**
 * Merge Bar / Cuisine / Grill into a location list (restaurant only).
 * Non-restaurant: returns existing list unchanged (no triad).
 *
 * @param {unknown} existing
 * @param {unknown} [businessType] when set and not restaurant → no-op
 * @returns {string[]}
 */
export function ensureRestaurantStockLocations(existing = [], businessType = null) {
  const list = Array.isArray(existing) ? existing : [];
  if (businessType != null && !isRestaurantBusinessType(businessType)) {
    return list.map((loc) => normalizeStockLocationKey(loc) || String(loc)).filter(Boolean);
  }

  const seen = new Set();
  const out = [];

  const push = (loc) => {
    const key = normalizeStockLocationKey(loc);
    if (!key || seen.has(key.toLowerCase())) return;
    seen.add(key.toLowerCase());
    out.push(key);
  };

  for (const loc of RESTAURANT_STOCK_LOCATIONS) push(loc);
  for (const loc of list) push(loc);

  out.sort((a, b) => {
    const ia = LOCATION_ORDER.indexOf(a);
    const ib = LOCATION_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });

  return out;
}

/**
 * @param {unknown} a
 * @param {unknown} b
 */
export function stockLocationListsEqual(a, b) {
  const aa = Array.isArray(a) ? a.map((x) => normalizeStockLocationKey(x)) : [];
  const bb = Array.isArray(b) ? b.map((x) => normalizeStockLocationKey(x)) : [];
  if (aa.length !== bb.length) return false;
  return aa.every((v, i) => v === bb[i]);
}

/**
 * @param {unknown} businessType
 * @param {unknown} stockLocations
 */
export function prepareRestaurantStockLocationsOnLoad(businessType, stockLocations) {
  if (!isRestaurantBusinessType(businessType)) {
    return {
      stockLocations: Array.isArray(stockLocations) ? stockLocations : [],
      seeded: false
    };
  }
  const next = ensureRestaurantStockLocations(stockLocations, businessType);
  const prev = Array.isArray(stockLocations) ? stockLocations : [];
  return {
    stockLocations: next,
    seeded: !stockLocationListsEqual(prev, next)
  };
}

function readExplicitProductLocation(product) {
  if (!product || typeof product !== 'object') return null;
  for (const c of [
    product.defaultStockLocation,
    product.stockLocation,
    product.storageLocation,
    product.kitchenStation,
    product.station
  ]) {
    const key = normalizeStockLocationKey(c);
    if (key && (RESTAURANT_STOCK_LOCATIONS.includes(key) || key === 'Principal')) {
      return key;
    }
  }
  return null;
}

function inferStationFromProductMeta(product) {
  if (!product || typeof product !== 'object') return null;
  const blob = [
    product.kitchenStation,
    product.station,
    product.subcategory,
    product.menuSection,
    product.category,
    product.type,
    product.name
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!blob) return null;
  if (/\bgrill|bbq|plancha|braise\b/.test(blob)) return RESTAURANT_LOCATION_GRILL;
  if (/\bbar|boisson|drink|cola|soft|bi[eè]re|beer\b/.test(blob)) {
    return RESTAURANT_LOCATION_BAR;
  }
  return null;
}

function nonRestaurantLineLocation(product, selectedOrFallback = '') {
  const selected = normalizeStockLocationKey(selectedOrFallback);
  if (selected) return selected;
  const stored = normalizeStockLocationKey(
    product?.defaultStockLocation || product?.stockLocation || product?.location || ''
  );
  return stored || 'Principal';
}

/**
 * Where product lots live (POS stock line default / catalog).
 * Restaurant-only heuristics (Bar / Grill / sells-on-POS → Bar).
 *
 * @param {object|null|undefined} product
 * @param {{ fallback?: string, businessType?: unknown, restaurantContext?: boolean }} [opts]
 */
export function resolveProductDefaultStockLocation(product, opts = {}) {
  const fallback = normalizeStockLocationKey(opts.fallback) || 'Principal';
  if (!isRestaurantStockLocationContext(opts)) {
    return nonRestaurantLineLocation(product, fallback);
  }

  if (isRestaurantDishProduct(product)) return fallback;

  const explicit = readExplicitProductLocation(product);
  if (explicit) return explicit;
  if (isRestaurantSellableStockProduct(product)) return RESTAURANT_LOCATION_BAR;

  const inferred = inferStationFromProductMeta(product);
  if (inferred) return inferred;
  return fallback;
}

/**
 * POS sell of a stock product (drinks prefer Bar) — restaurant only.
 * @param {object|null|undefined} product
 * @param {string} [selectedPosLocation]
 * @param {{ businessType?: unknown, restaurantContext?: boolean }} [opts]
 */
export function resolvePosLineStockLocation(product, selectedPosLocation = '', opts = {}) {
  if (!isRestaurantStockLocationContext(opts)) {
    return nonRestaurantLineLocation(product, selectedPosLocation);
  }

  const explicit = readExplicitProductLocation(product);
  if (explicit) return explicit;
  if (isRestaurantSellableStockProduct(product)) return RESTAURANT_LOCATION_BAR;
  const selected = normalizeStockLocationKey(selectedPosLocation);
  if (selected) return selected;
  return resolveProductDefaultStockLocation(product, opts);
}

/**
 * Recipe ingredient demand location (does not inherit POS Bar filter).
 * Restaurant only; else sale/cart location or Principal.
 *
 * @param {object|null|undefined} ingredientProduct
 * @param {string} [saleFallback]
 * @param {{ businessType?: unknown, restaurantContext?: boolean }} [opts]
 */
export function resolveIngredientDemandLocation(
  ingredientProduct,
  saleFallback = '',
  opts = {}
) {
  if (!isRestaurantStockLocationContext(opts)) {
    return nonRestaurantLineLocation(ingredientProduct, saleFallback);
  }

  const explicit = readExplicitProductLocation(ingredientProduct);
  if (explicit) return explicit;
  if (isRestaurantSellableStockProduct(ingredientProduct)) return RESTAURANT_LOCATION_BAR;

  const inferred = inferStationFromProductMeta(ingredientProduct);
  if (inferred) return inferred;

  const fallback = normalizeStockLocationKey(saleFallback);
  if (
    fallback === RESTAURANT_LOCATION_CUISINE ||
    fallback === RESTAURANT_LOCATION_GRILL ||
    fallback === 'Principal'
  ) {
    return fallback;
  }
  return RESTAURANT_LOCATION_CUISINE;
}

/**
 * Ensure Bar / Cuisine / Grill exist; persist when missing.
 *
 * @returns {Promise<{ locations: string[], activated: boolean, alreadyActive: boolean }>}
 */
export async function activateRestaurantStockLocations({
  businessType,
  businessId = null,
  currentLocations = [],
  setLocations = null,
  persist = true
} = {}) {
  if (!isRestaurantBusinessType(businessType)) {
    const list = Array.isArray(currentLocations) ? currentLocations.map(String) : [];
    return { locations: list, activated: false, alreadyActive: true };
  }

  const before = Array.isArray(currentLocations) ? currentLocations : [];
  const next = ensureRestaurantStockLocations(before, businessType);
  const alreadyActive = stockLocationListsEqual(before, next);

  if (typeof setLocations === 'function') setLocations(next);

  if (persist && !alreadyActive) {
    try {
      const { localStorageManager } = await import('../../services/localStorageManager');
      const { getCurrentBusinessId } = await import('../../utils/sharedCore');
      const id = businessId || getCurrentBusinessId({ allowFallback: true });
      if (id) {
        await localStorageManager.setBusinessData('stockLocations', next, id);
      }
      try {
        const { refreshStockLocations } = await import('../../services/cacheReadModel');
        if (typeof refreshStockLocations === 'function') refreshStockLocations(next);
      } catch {
        /* cache optional */
      }
      try {
        const { publishStockLocationsUpdated } = await import(
          '../../infrastructure/events/legacyEventBridge'
        );
        if (typeof publishStockLocationsUpdated === 'function') {
          publishStockLocationsUpdated({ action: 'locationAdded', locations: next });
        }
      } catch {
        /* event optional */
      }
    } catch (err) {
      console.warn('[restaurantStockLocations] activate failed:', err?.message || err);
    }
  }

  return { locations: next, activated: !alreadyActive, alreadyActive };
}
